import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const Payments = () => {
  const BASE_URL = "";
  const [balance, setBalance] = useState(null);
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    fetchPaymentData();
  }, []);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      
      // Fetch balance, packages, and history in parallel
      const [balanceResponse, packagesResponse, historyResponse] = await Promise.all([
        fetch(`${BASE_URL}/api/payments/balance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BASE_URL}/api/payments/packages`),
        fetch(`${BASE_URL}/api/payments/history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (balanceResponse.ok) {
        const balanceData = await balanceResponse.json();
        setBalance(balanceData.balance);
      }

      if (packagesResponse.ok) {
        const packagesData = await packagesResponse.json();
        setPackages(packagesData.packages || []);
      }

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setHistory(historyData.transactions || []);
      }

      setError(null);
    } catch (error) {
      setError('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageData) => {
    try {
      setPurchasing(packageData.id);
      setError(null);

      const response = await fetch(`${BASE_URL}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: packageData.total })
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        setError(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setPurchasing(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    if (type === 'purchase') {
      return (
        <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      );
    } else {
      return (
        <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
        </svg>
      );
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-32 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="gradient-bg overflow-hidden shadow-skilltell-lg rounded-xl">
        <div className="px-4 py-5 sm:p-6">
          <h1 className="text-2xl font-bold text-white">Payments & Credits</h1>
          <p className="mt-1 text-sm text-primary-100">
            Manage your account balance and purchase advertising credits
          </p>
        </div>
      </div>

      {/* Balance Overview */}
      {balance && (
        <div className="bg-white shadow-skilltell rounded-xl">
          <div className="px-4 py-5 sm:p-6">
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-green-600">Current Balance</div>
                    <div className="text-2xl font-bold text-green-900">{formatCurrency(balance.current)}</div>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-primary-600">Total Purchased</div>
                    <div className="text-2xl font-bold text-primary-900">{formatCurrency(balance.total_purchased)}</div>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-purple-600">Total Spent</div>
                    <div className="text-2xl font-bold text-purple-900">{formatCurrency(balance.total_spent)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Credit Packages */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Purchase Credits</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg) => (
              <div 
                key={pkg.id} 
                className={`relative rounded-lg border-2 p-6 cursor-pointer hover:shadow-lg transition-shadow ${
                  pkg.popular ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-500 text-white px-3 py-1 text-xs font-medium rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{pkg.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">{formatCurrency(pkg.amount)}</span>
                    {pkg.bonus > 0 && (
                      <div className="text-sm text-green-600 font-medium">
                        + {formatCurrency(pkg.bonus)} bonus
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mb-6">{pkg.description}</p>
                  
                  <div className="mb-6">
                    <div className="text-lg font-bold text-gray-900">
                      Total: {formatCurrency(pkg.total)}
                    </div>
                    {pkg.bonus > 0 && (
                      <div className="text-sm text-green-600">
                        Save {formatCurrency(pkg.bonus)}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handlePurchase(pkg)}
                    disabled={purchasing === pkg.id}
                    className={`w-full py-2 px-4 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      pkg.popular
                        ? 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500'
                        : 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {purchasing === pkg.id ? 'Processing...' : 'Purchase'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">Payment History</h2>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-primary-600 hover:text-primary-900 text-sm font-medium"
            >
              {showHistory ? 'Hide History' : 'Show History'}
            </button>
          </div>

          {showHistory && (
            <div className="space-y-4">
              {history.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions</h3>
                  <p className="mt-1 text-sm text-gray-500">Your payment history will appear here.</p>
                </div>
              ) : (
                <div className="overflow-hidden bg-white shadow sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {history.map((transaction) => (
                      <li key={transaction.id}>
                        <div className="px-4 py-4 flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              {getTransactionIcon(transaction.transaction_type)}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {transaction.transaction_type === 'purchase' ? 'Credit Purchase' : 'Campaign Spend'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {formatDate(transaction.created_at)}
                              </div>
                              {transaction.payment_method && (
                                <div className="text-xs text-gray-400 capitalize">
                                  via {transaction.payment_method}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-sm font-medium ${
                              transaction.transaction_type === 'purchase' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {transaction.transaction_type === 'purchase' ? '+' : '-'}
                              {formatCurrency(Math.abs(transaction.amount))}
                            </div>
                            <div className="text-xs text-gray-500">
                              Balance: {formatCurrency(transaction.balance)}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Payments;